import React, {FunctionComponent, useEffect, useState} from 'react';
import {Modal} from 'react-bootstrap';
import {RouteComponentProps, useHistory, useParams} from 'react-router-dom';
import {useForm} from "react-hook-form";
import {Trans, useTranslation} from 'react-i18next';

import './EditDevice.scss';
import {Breadcrumbs, Button, ErrorMessage, FormInput, FormSelect, Loader, Page, PageHeader} from 'components';
import {IDevice, IDeviceModel, IFirmware, IManufacturer} from 'interfaces';
import {ErrorType, LoadState} from 'enums';
import {DeviceInfoService, DevicesService, parseAxiosError} from 'services';
import {useCancelablePromise} from 'hooks';

type IDeviceForm = {
    manufacturerId: string;
    modelId: string;
    firmwareId: string;
    location: string;
    serial_number: string;
    mac_address: string;
}

export const EditDevice: FunctionComponent<{}> = (props) => {
    const { cancelablePromise } = useCancelablePromise();
    const form = useForm<IDeviceForm>({mode: 'onTouched'});
    const { t, i18n } = useTranslation();
    const history = useHistory();
    let urlParams = useParams<{ id: string }>();
    const [loadState, setLoadState] = useState(LoadState.Loading);
    const [saveState, setSaveState] = useState(LoadState.Initial);
    const [deleteState, setDeleteState] = useState(LoadState.Initial);
    const [loadError, setLoadError] = useState('');
    const [saveError, setSaveError] = useState('');
    const [deleteError, setDeleteError] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const isAPICall = saveState === LoadState.Loading || deleteState === LoadState.Loading;
    // Watch the manufacturer field so we can update the model/firmware when it changes
    // The type assertions are because react-hook-form's type validation isn't working correctly,
    // and we want to get a compile error if a field is ever renamed.
    const field_manufacturerId: keyof IDeviceForm = 'manufacturerId';
    const field_modelId: keyof IDeviceForm = 'modelId';
    const field_firmwareId: keyof IDeviceForm = 'firmwareId';
    const manufacturerId = form.watch(field_manufacturerId, undefined);

    const isNew = urlParams.id === 'new';
    let id: number;
    if (!isNew) {
        id = parseInt(urlParams.id, 10);
    }

    const mapDeviceToForm = (d: IDevice): IDeviceForm => {
        return {
            // Browser selects and react-hook-form only support strings, so need to map.
            manufacturerId: d.manufacturer?.id + '',
            modelId: d.model?.id + '',
            firmwareId: d.firmware?.id + '',
            location: d.location,
            serial_number: d.serial_number,
            mac_address: d.mac_address,
        }
    }

    const mapFormToDevice = (formData: IDeviceForm): IDevice => {
        // Note: man/model/firmware ids will be undefined for existing devices,
        // because browsers don't submit the values of disabled fields.
        const manufacturerId = parseInt(formData.manufacturerId, 10);
        return {
            id: id,
            manufacturer: {
                id: manufacturerId,
            },
            model: {
                id: parseInt(formData.modelId, 10),
                manufacturer_id: manufacturerId,
            },
            firmware: {
                id: parseInt(formData.firmwareId, 10),
                manufacturer_id: manufacturerId,
            },
            location: formData.location,
            serial_number: formData.serial_number,
            mac_address: formData.mac_address,
        };
    }

    // Load the manufacturers/makes/models and the device
    useEffect(() => {
        let promises = [cancelablePromise(DeviceInfoService.getManufacturerMap())];
        if (!isNew) {
            promises.push(cancelablePromise(DevicesService.getDevice(id)))
        }
        Promise.all(promises)
            .then(([deviceInfo, device]) => {
                if (isNew) {
                    const mans = DeviceInfoService.manufacturers;
                    // Vendors are only going to have 1 manufacturer, so autopopulate our values if we only have 1.
                    if (mans.length === 1) {
                        const formVals: Partial<IDeviceForm> = {};
                        const man = mans[0];
                        formVals.manufacturerId = man.id + '';

                        if (man.models.length === 1) {
                            formVals.modelId = man.models[0].id + '';
                        }
                        if (man.firmwares.length === 1) {
                            formVals.firmwareId = man.firmwares[0].id + '';
                        }

                        form.reset(formVals);
                    }
                } else { 
                    form.reset(mapDeviceToForm(device));
                }
                setLoadState(LoadState.Success);
            })
            .catch((err) => {
                console.log('ERROR loading device info: ', err);
                setLoadError(parseAxiosError(err));
                setLoadState(LoadState.Error);
            });
    }, []);

    const onManufacturerChange = () => {
        // Need to clear the model and firmware selects when the manufacturer changes.
        form.setValue(field_modelId, '');
        form.setValue(field_firmwareId, '');
    }

    let isSubmitting = false;
    const onSubmit = (formData: IDeviceForm) => {
        if (isSubmitting || loadState !== LoadState.Success || saveState === LoadState.Loading) return;
        // "setState" is async, so set a var immediately to prevent possible double submit.
        isSubmitting = true;
        setSaveError('');
        setSaveState(LoadState.Loading);

        const deviceFromForm = mapFormToDevice(formData);

        const p = cancelablePromise(isNew
            ? DevicesService.createDevice(deviceFromForm)
            : DevicesService.updateDevice(deviceFromForm)
        );
        p.then((updatedDevice) => {
                setSaveState(LoadState.Success);
                // Update the form with the returned object from the server.
                form.reset(mapDeviceToForm(updatedDevice));
                history.push('/');
            })
            .catch((err) => {
                console.log('ERROR saving: ', err);
                setSaveState(LoadState.Error);
                setSaveError(parseAxiosError(err));
            })
    }

    const onCancel = () => {
        // No need to do anything, just go back to devices
        history.push('/');
    }

    const onOpenDeleteModal = () => {
        // Clear any errors
        if (deleteState === LoadState.Error) {
            setDeleteState(LoadState.Initial);
            setDeleteError('');
        }
        setShowDeleteModal(true);
    }

    const onDelete = () => {
        if (isSubmitting) return;
        if (loadState !== LoadState.Success) return;
        if (saveState === LoadState.Loading) return;
        // "setState" is async, so set a var immediately to prevent possible double submit.
        isSubmitting = true;
        setDeleteError('');
        setDeleteState(LoadState.Loading);

        cancelablePromise(DevicesService.deleteDevice(id))
            .then(() => {
                history.push('/');
            })
            .catch((err) => {
                console.log('ERROR deleting: ', err);
                setDeleteState(LoadState.Error);
                setDeleteError(parseAxiosError(err));
            })
    }

    const requestDeleteModalClose = () => {
        // Ignore close requests if we're currently deleting
        if (deleteState !== LoadState.Loading) {
            setShowDeleteModal(false);
        }
    }

    const breadcrumbs = (
        <Breadcrumbs crumbs={[
            {
                label: t('devicesList.title'),
                url: '/',
            },
        ]} 
        dataCy="edit_breadcrumb"
        />
    );

    let content = undefined;
    switch (loadState) {
        case LoadState.Initial:
        case LoadState.Loading:
            content = <Loader />;
            break;
        case LoadState.Success: {
            let filteredModels: IDeviceModel[] = [];
            let filteredFirmwares: IFirmware[] = [];
            if (manufacturerId) {
                const man = DeviceInfoService.manufacturersById[manufacturerId];
                if (man) {
                    filteredModels = man.models;
                    filteredFirmwares = man.firmwares;
                } else {
                    console.error('Manufacturer has invalid id: ', manufacturerId);
                }
            }

            content = (<>
                {saveState === LoadState.Error && (
                    <ErrorMessage type={ErrorType.Generic} message={saveError} />
                )}
                <p>
                    Please enter your device's information. Then, please reach out in your Slack channel to have
                    your device's metadata updated to direct it to Certifier rather than RIOT. Once you
                    have completed testing, if you need to re-connect the device to RIOT, you'll need to reach out
                    to have its metadata switched back.
                </p>
                <form onSubmit={form.handleSubmit(onSubmit)} data-cy="edit_form">
                    <div className="row">
                        <div className="col">
                            <FormSelect
                                disabled={!isNew || isAPICall}
                                form={form}
                                getOptionLabel={man => man.name}
                                getOptionValue={man => man.id + ''}
                                label={t('editDevice.fields.manufacturer')}
                                name="manufacturerId"
                                onChange={onManufacturerChange}
                                options={DeviceInfoService.manufacturers}
                                registerOptions={{required: true}}
                                dataCy="edit_manufacturerDropdown"
                            />
                        </div>
                        <div className="col">
                            <FormInput
                                disabled={isAPICall}
                                form={form}
                                label={t('editDevice.fields.serial')}
                                maxLength={128}
                                name="serial_number"
                                registerOptions={{required: true}}
                                dataCy="edit_serialNumberInput"
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col">
                            <FormSelect
                                disabled={!isNew || !manufacturerId || isAPICall}
                                form={form}
                                getOptionLabel={model => model.name}
                                getOptionValue={model => model.id + ''}
                                label={t('editDevice.fields.model')}
                                name="modelId"
                                options={filteredModels}
                                registerOptions={{required: true}}
                                dataCy="edit_modelDropdown"
                            />
                        </div>
                        <div className="col">
                            <FormInput
                                disabled={isAPICall}
                                form={form}
                                label={t('editDevice.fields.location')}
                                maxLength={5}
                                name="location"
                                registerOptions={{
                                    required: true,
                                    pattern: {
                                        // Must be 5 digits
                                        value: /^\d{5}$/,
                                        message: t('errors.validation.location'),
                                    },
                                }}
                                dataCy="edit_locationInput"
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col">
                            <FormSelect
                                disabled={!isNew || !manufacturerId || isAPICall}
                                form={form}
                                getOptionLabel={f => f.firmware_version}
                                getOptionValue={f => f.id + ''}
                                label={t('editDevice.fields.firmware')}
                                name="firmwareId"
                                options={filteredFirmwares}
                                registerOptions={{required: true}}
                                dataCy="edit_firmwareDropdown"
                            />
                        </div>
                        <div className="col">
                            <FormInput
                                disabled={isAPICall}
                                form={form}
                                label={t('editDevice.fields.macAddress')}
                                maxLength={128}
                                name="mac_address"
                                registerOptions={{
                                    pattern: {
                                        // Looks like "a1:b2:c3:d4:e5:f6", case insensitive.
                                        value: /^([0-9a-f]{2}:){5}[0-9a-f]{2}$/i,
                                        message: t('errors.validation.macAddress'),
                                    },
                                }}
                                dataCy="edit_macAddressInput"
                            />
                        </div>
                    </div>
                    <p className="editDevice_formNote">
                        <Trans
                            i18nKey="editDevice.formNote"
                            components={{bold: <strong />}}
                        />
                    </p>
                    {loadState === LoadState.Success && (
                        <div className="formFooter">
                            {isNew && (
                                <Button theme="text" type="button" disabled={isAPICall} onClick={onCancel} dataCy="edit_cancelButton">
                                    {t('button.cancel')}
                                </Button>
                            )}
                            {!isNew && (
                                <Button theme="text" type="button" disabled={isAPICall} onClick={onOpenDeleteModal} dataCy="edit_deleteButton">
                                    {t('button.delete')}
                                </Button>
                            )}
                            <Button theme="red" type="submit" disabled={isAPICall} dataCy="edit_submitButton">
                                {saveState === LoadState.Loading
                                    ? t('button.saving')
                                    : t('button.save')}
                            </Button>
                        </div>
                    )}
                </form>
            </>);
            break;
        }
        case LoadState.Error:
            content = <ErrorMessage type={ErrorType.Generic} message={loadError} dataCy="edit_loadError"/>
            break;
    }

    return (
        <Page>
            <PageHeader
                top={breadcrumbs}
                title={isNew ? t('editDevice.newTitle') : t('editDevice.title')}
            />
            {content}
            <Modal show={showDeleteModal} onHide={requestDeleteModalClose}>
                <Modal.Header closeButton>
                    <Modal.Title>{t('editDevice.deleteModal.header')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {deleteState === LoadState.Error && (
                        <ErrorMessage type={ErrorType.Generic} message={deleteError} />
                    )}
                    {t('editDevice.deleteModal.body')}
                </Modal.Body>
                <Modal.Footer>
                    <Button theme="text" onClick={requestDeleteModalClose} disabled={isAPICall} dataCy="deleteModal_cancelButton">
                        {t('button.cancel')}
                    </Button>
                    <Button theme="red" onClick={onDelete} disabled={isAPICall} dataCy="deleteModal_confirmButton">
                        {deleteState === LoadState.Loading
                            ? t('button.deleting')
                            : t('button.delete')
                        }
                    </Button>
                </Modal.Footer>
            </Modal>
        </Page>
    );
}